import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch, API } from '../api';
import { displayLocation } from '../location';

// Warehouse → Calibrate → a face, unit by unit (docs/WAREHOUSE-MODEL.md). The sections come
// from Fernando's measurements; the walk records what stands there: per metal bay the system,
// setup, shelves, level heights (top of shelf to top of shelf, the first from the floor), cross
// bracing (R3000) or the beam count (HI280); per pallet the setup and how many sections it
// covers. Every unit starts as a copy of the previous one, so a run of identical bays is
// "✓ Next" per bay. Autosaves; a manager/admin applies. Claude, 2026-09-25.

interface Stock { pieces: number; styles: number; top_brand?: string | null }
interface Section { id: number; sid: string; code: string; from_top_cm: number | null; length_cm: number | null;
                    measured: boolean; unit_id: string | null; note: string | null; floor_space?: boolean; stock: Stock }
interface Gap { after_code: string | null; before_code: string | null; length_cm: number | null; reason: string; note: string | null }
interface Face { letter: string; row: string; kind: 'pallet' | 'metal'; aisle: string; system: string | null; numbering: string }
interface U {
  key: string; count: number; kind: 'pallet' | 'bay'; setup: string | null; system: string | null;
  shelves: number | null; levels_cm: (number | null)[]; cross_bracing: boolean | null; beams: number | null;
  note: string; done?: boolean;
}
interface FaceView { face: Face; sections: Section[]; gaps: Gap[]; units: Record<string, any>;
                     setups: Record<string, { kind: string | null; footprint: number }>; gap_reasons: string[] }

const PALLET_SETUPS = ['S', 'M', 'SL', 'LL', 'ML', 'other'];
const BAY_SETUPS = ['tee', 'hoodie', 'tee_special_B', 'other'];
const LABEL: Record<string, string> = { S: 'S', M: 'M', SL: 'SL', LL: 'LL', ML: 'ML·S', tee: 'Tee', hoodie: 'Hoodie',
                                       tee_special_B: 'B-special', other: 'Other', empty: 'Empty' };
const role = () => localStorage.getItem('wms_role') || 'staff';
const isDevice = () => !!localStorage.getItem('wms_is_device');
const newKey = () => `n${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function gapBetween(gaps: Gap[], a: string, b: string) {
  return gaps.find(g => (g.after_code === a && g.before_code === b) || (g.after_code === b && g.before_code === a));
}

/** Default units for sections[from…]: a bay per section; a pallet covers its setup's footprint,
 *  never across a gap. Each copies `proto`. */
function regroup(v: FaceView, from: number, proto: Partial<U>): U[] {
  const out: U[] = [];
  const secs = v.sections;
  let i = from;
  while (i < secs.length) {
    let n = 1;
    if (secs[i].floor_space) {                       // open floor, no shelf: its own spot (A 1)
      out.push({ ...blank(v, 1, {}), kind: 'pallet', system: null, shelves: null, levels_cm: [],
                 cross_bracing: null, beams: null });
      i += 1;
      continue;
    }
    if (v.face.kind === 'pallet') {
      const fp = (proto.setup && v.setups[proto.setup]?.footprint) || 3;
      while (n < fp && i + n < secs.length && !gapBetween(v.gaps, secs[i + n - 1].code, secs[i + n].code)) n++;
    }
    out.push(blank(v, n, proto));
    i += n;
  }
  return out;
}

function blank(v: FaceView, count: number, proto: Partial<U>): U {
  const bay = v.face.kind === 'metal';
  return {
    key: newKey(), count, kind: bay ? 'bay' : 'pallet',
    setup: proto.setup ?? null, system: bay ? (proto.system ?? v.face.system) : null,
    shelves: bay ? (proto.shelves ?? null) : null, levels_cm: bay ? [...(proto.levels_cm || [])] : [],
    cross_bracing: bay ? (proto.cross_bracing ?? null) : null, beams: bay ? (proto.beams ?? null) : null, note: '',
  };
}

/** Units already recorded on the face become the starting point; the rest is regrouped. */
function initial(v: FaceView): U[] {
  const out: U[] = [];
  let i = 0;
  while (i < v.sections.length) {
    const uid = v.sections[i].unit_id;
    if (!uid || !v.units[uid]) {
      const j = v.sections.findIndex((s, k) => k > i && s.unit_id);
      const end = j < 0 ? v.sections.length : j;
      const part = regroup({ ...v, sections: v.sections.slice(0, end) }, i, out.length ? out[out.length - 1] : {});
      out.push(...part);
      i = end;
      continue;
    }
    let n = 1;
    while (i + n < v.sections.length && v.sections[i + n].unit_id === uid) n++;
    const e = v.units[uid];
    out.push({ key: uid, count: n, kind: e.kind, setup: e.setup, system: e.system, shelves: e.shelves,
               levels_cm: e.levels_cm || [], cross_bracing: e.cross_bracing, beams: e.beams, note: e.note || '', done: true });
    i += n;
  }
  return out;
}

export default function UnitsWalk() {
  const { letter = '' } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState<FaceView | null>(null);
  const [walkId, setWalkId] = useState<number | null>(null);
  const [actor, setActor] = useState<string | null>(null);
  const [needActor, setNeedActor] = useState(false);
  const [people, setPeople] = useState<{ username: string; name: string }[]>([]);
  const [units, setUnits] = useState<U[]>([]);
  const [gaps, setGaps] = useState<Gap[] | null>(null);
  const [pos, setPos] = useState(0);
  const [err, setErr] = useState('');
  const [save, setSave] = useState('');
  const [preview, setPreview] = useState<any | null>(null);
  const [applied, setApplied] = useState<any | null>(null);
  const ready = useRef(false);

  useEffect(() => {
    (async () => {
      const r = await authFetch(`${API}/warehouse/faces/${encodeURIComponent(letter)}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || 'Could not load the face'); return; }
      setV(d);
      if (isDevice()) {
        setNeedActor(true);
        const p = await authFetch(`${API}/pp/people`).then(x => x.json()).catch(() => ({ people: [] }));
        setPeople(p.people || []);
      } else start(d, null);
    })();
  }, [letter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function start(view: FaceView, who: string | null) {
    const r = await authFetch(`${API}/warehouse/walks`, { method: 'POST',
      body: JSON.stringify({ mode: 'units', face: letter, actor: who }) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(d.detail || 'Could not start the walk'); return; }
    setWalkId(d.walk.id); setActor(d.walk.actor);
    const data = d.walk.data || {};
    if (d.resumed && (data.units || []).length) {
      setUnits(data.units.map((u: any) => ({ ...u, levels_cm: u.levels_cm || [], note: u.note || '' })));
      setGaps(data.gaps ?? null);
      setPos(Math.min(data.position || 0, data.units.length - 1));
    } else {
      setUnits(initial(view));
    }
    ready.current = true;
  }

  useEffect(() => {
    if (!walkId || !ready.current || applied) return;
    setSave('Saving…');
    const t = setTimeout(async () => {
      const r = await authFetch(`${API}/warehouse/walks/${walkId}`, { method: 'PUT',
        body: JSON.stringify({ units, gaps, position: pos }) }).catch(() => null);
      setSave(r && r.ok ? 'Saved' : 'Not saved — check the connection');
    }, 1000);
    return () => clearTimeout(t);
  }, [units, gaps, pos, walkId, applied]);

  const liveGaps = gaps ?? v?.gaps ?? [];
  const starts = useMemo(() => { const s: number[] = []; let i = 0; for (const u of units) { s.push(i); i += u.count; } return s; }, [units]);
  const covered = units.reduce((n, u) => n + u.count, 0);

  if (err && !v) return <div className="p-6 text-red-400">{err}</div>;
  if (!v) return <div className="p-6 text-gray-500">Loading…</div>;
  if (needActor && !walkId) return (
    <div className="p-6 max-w-xl">
      <h2 className="text-xl font-semibold text-white mb-4">Who is walking?</h2>
      <div className="grid grid-cols-2 gap-3">
        {people.map(p => <button key={p.username} onClick={() => { setNeedActor(false); start(v, p.username); }}
          className="rounded-xl bg-gray-800 hover:bg-blue-600 text-white text-lg py-4">{p.name}</button>)}
      </div>
      {err && <div className="text-red-400 text-sm mt-3">{err}</div>}
    </div>
  );
  if (!walkId || !units.length) return <div className="p-6 text-gray-500">{err || 'Starting…'}</div>;

  if (applied) return (
    <div className="p-6 max-w-2xl">
      <h2 className="text-xl font-semibold text-emerald-300 mb-2">Face {v.face.letter} applied</h2>
      <div className="text-sm text-gray-300">{applied.units} units ({applied.units_new} new, {applied.units_paired} paired,
        {' '}{applied.units_updated} updated){applied.legs ? ` · ${applied.legs.legs} legs` : ''}</div>
      <button onClick={() => navigate('/warehouse/calibrate')} className="mt-5 px-4 py-2 rounded-lg bg-blue-600 text-white">Next face</button>
    </div>
  );

  if (preview) return (
    <div className="p-5 max-w-4xl">
      <button onClick={() => setPreview(null)} className="text-gray-400 hover:text-white text-sm">← Back to the walk</button>
      <h2 className="text-xl font-semibold text-white mt-1 mb-2">Face {v.face.letter} — what Apply records</h2>
      <div className="text-sm text-gray-300 mb-3">
        {preview.counts.units} units over {preview.counts.sections} sections · {preview.counts.units_new} new ·
        {' '}{preview.counts.units_paired} paired with the other face · {preview.counts.units_updated} updated · {preview.counts.gaps} gaps
        {preview.legs && <> · <b>{preview.legs.legs} legs</b> ({preview.legs.runs} run{preview.legs.runs === 1 ? '' : 's'}
          {preview.legs.breaks.length ? `, extra at ${preview.legs.breaks.map(displayLocation).join(', ')}` : ''})</>}
      </div>
      {preview.errors.length > 0 && <div className="rounded-lg bg-red-900/30 border border-red-700 p-3 mb-3 text-sm text-red-200">
        {preview.errors.map((e: string, i: number) => <div key={i}>{e}</div>)}</div>}
      {preview.warnings.length > 0 && <div className="rounded-lg bg-amber-900/20 border border-amber-700 p-3 mb-3 text-sm text-amber-200">
        {preview.warnings.map((e: string, i: number) => <div key={i}>{e}</div>)}</div>}
      <div className="text-sm text-gray-300 max-h-96 overflow-auto space-y-0.5">
        {preview.units.map((u: any, i: number) => (
          <div key={i}>
            <span className={u.action === 'create' ? 'text-emerald-300' : u.action === 'pair' ? 'text-blue-300' : 'text-gray-400'}>
              {u.action === 'create' ? 'new' : u.action}</span> {u.id || ''} · {u.codes.map(displayLocation).join(', ')} · {LABEL[u.fields.setup] || u.fields.setup}
            {u.kind === 'bay' && <span className="text-gray-400"> · {u.fields.system} · {u.fields.shelves ?? '?'} shelves
              {u.fields.levels_cm?.length ? ` (${u.fields.levels_cm.join(' / ')} cm)` : ''}
              {u.fields.system === 'R3000' ? ` · cross ${u.fields.cross_bracing === null ? '?' : u.fields.cross_bracing ? 'yes' : 'no'}` : ''}
              {u.fields.system === 'HI280' ? ` · ${u.fields.beams ?? '?'} beams` : ''}</span>}
          </div>
        ))}
      </div>
      {err && <div className="text-red-400 text-sm mt-3">{err}</div>}
      {(role() === 'admin' || role() === 'manager')
        ? <button disabled={preview.errors.length > 0} onClick={async () => {
            setErr('');
            const r = await authFetch(`${API}/warehouse/walks/${walkId}/apply`, { method: 'POST' });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) setErr(d.detail || 'Apply failed'); else setApplied(d.summary);
          }} className="mt-5 px-5 py-3 rounded-xl bg-emerald-600 text-white font-semibold disabled:opacity-40">Apply to the warehouse model</button>
        : <div className="mt-5 text-sm text-gray-400">The walk is saved. A manager applies it from Warehouse → Calibrate.</div>}
    </div>
  );

  const u = units[pos];
  const secs = v.sections.slice(starts[pos], starts[pos] + u.count);
  const prevLast = starts[pos] > 0 ? v.sections[starts[pos] - 1] : null;
  const gapBefore = prevLast && secs[0] ? gapBetween(liveGaps, prevLast.code, secs[0].code) : undefined;
  const bay = u.kind === 'bay';
  const setU = (patch: Partial<U>) => setUnits(us => us.map((x, i) => (i === pos ? { ...x, ...patch } : x)));

  function setCount(n: number) {
    const max = v!.sections.length - starts[pos];
    n = Math.max(1, Math.min(n, max));
    if (n === u.count) return;
    if (units.slice(pos + 1).some(x => x.done) && !window.confirm('This regroups the units after this one. Continue?')) return;
    setUnits(us => [...us.slice(0, pos), { ...us[pos], count: n }, ...regroup(v!, starts[pos] + n, { ...us[pos] })]);
  }
  function setSetup(s: string) {
    const fp = v!.setups[s]?.footprint || 1;
    if (!bay && fp !== u.count) {
      setUnits(us => [...us.slice(0, pos), { ...us[pos], setup: s, count: Math.min(fp, v!.sections.length - starts[pos]) },
                      ...regroup(v!, starts[pos] + Math.min(fp, v!.sections.length - starts[pos]), { ...us[pos], setup: s })]);
    } else setU({ setup: s });
  }
  function setShelves(n: number | null) {
    const k = n === null ? 0 : Math.max(0, Math.min(12, n));
    const lv = [...u.levels_cm]; while (lv.length < k) lv.push(null); lv.length = k;
    // HI280: every shelf needs its back beam (Fernando, 2026-09-25), so the beam count follows
    // the shelves until someone sets a different number
    const follow = u.system === 'HI280' && (u.beams === null || u.beams === u.shelves);
    setU({ shelves: n === null ? null : k, levels_cm: lv, ...(follow ? { beams: n === null ? null : k } : {}) });
  }
  function next() {
    setUnits(us => us.map((x, i) => {
      if (i === pos) return { ...x, done: true };
      if (i === pos + 1 && !x.done && x.kind === us[pos].kind && us[pos].setup !== 'empty') {   // a pallet on a floor space is not copied into the next bay
        const c = us[pos];
        return { ...x, setup: c.setup, system: c.system, shelves: c.shelves, levels_cm: [...c.levels_cm],
                 cross_bracing: c.cross_bracing, beams: c.beams };
      }
      return x;
    }));
    setPos(p => Math.min(p + 1, units.length - 1));
  }
  async function finish() {
    setErr('');
    await authFetch(`${API}/warehouse/walks/${walkId}`, { method: 'PUT', body: JSON.stringify({ units, gaps, position: pos }) });
    const r = await authFetch(`${API}/warehouse/walks/${walkId}/preview`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) setErr(d.detail || 'Preview failed'); else setPreview(d.change);
  }
  const editGap = (i: number, patch: Partial<Gap>) => setGaps((gaps ?? v.gaps).map((g, k) => (k === i ? { ...g, ...patch } : g)));

  const pieces = secs.reduce((n, s) => n + (s.stock?.pieces || 0), 0);
  const done = units.filter(x => x.done).length;
  const btn = (on: boolean) => `px-4 py-3 rounded-lg text-base ${on ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-100'}`;

  return (
    <div className="p-3 sm:p-4 max-w-4xl pb-28 md:pb-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <button onClick={() => navigate('/warehouse/calibrate')} className="text-gray-400 hover:text-white text-sm">← Faces</button>
          <h1 className="text-2xl font-bold text-white">Face {v.face.letter} <span className="text-base font-normal text-gray-400">
            · {v.face.kind === 'metal' ? `metal, ${v.face.system}` : 'pallets'} · aisle {v.face.aisle} · {actor}</span></h1>
        </div>
        <span className="text-sm text-gray-500">{save}</span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setPos(p => Math.max(0, p - 1))} disabled={pos === 0} className="px-4 py-3 rounded-lg bg-gray-700 text-white disabled:opacity-30">← Prev</button>
        <div className="flex-1 text-center">
          <div className="text-white text-lg font-semibold">{bay ? 'Bay' : 'Pallet'} {pos + 1} / {units.length}</div>
          <div className="text-xs text-gray-500">{done} confirmed · covers {covered} / {v.sections.length} sections</div>
        </div>
        <button onClick={() => setPos(p => Math.min(units.length - 1, p + 1))} disabled={pos >= units.length - 1} className="px-4 py-3 rounded-lg bg-gray-700 text-white disabled:opacity-30">Skip →</button>
      </div>

      {gapBefore && <div className="rounded-lg border border-fuchsia-700 bg-fuchsia-900/20 text-fuchsia-200 text-sm px-3 py-2 mb-3">
        Gap before this {bay ? 'bay' : 'pallet'}: {gapBefore.length_cm ?? '?'} cm · {gapBefore.reason}{gapBefore.note ? ` — ${gapBefore.note}` : ''}
        {bay && ' (a new run: one extra leg)'}</div>}

      <div className={`rounded-xl border-2 p-4 ${u.done ? 'border-emerald-600 bg-emerald-900/10' : 'border-gray-700 bg-gray-800/40'}`}>
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div className="text-3xl font-bold text-white">{secs.map(s => displayLocation(s.code)).join(' · ')}
            {secs[0]?.floor_space && <span className="ml-3 text-sm font-normal text-sky-300">floor space — no shelf</span>}</div>
          <div className="text-sm text-gray-400">{units[pos].key.startsWith('n') ? 'new' : units[pos].key} · {pieces} pcs</div>
        </div>
        <div className="text-xs text-gray-500 mt-1">{secs.map(s => `${s.sid} ${s.stock.pieces} pcs${s.stock.top_brand ? ` ${s.stock.top_brand}` : ''}`).join(' · ')}</div>

        {!bay && (
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-300">
            Sections on this pallet:
            <button onClick={() => setCount(u.count - 1)} className="px-3 py-2 rounded bg-gray-700">−</button>
            <b className="text-white text-lg w-6 text-center">{u.count}</b>
            <button onClick={() => setCount(u.count + 1)} className="px-3 py-2 rounded bg-gray-700">+</button>
          </div>
        )}

        <div className="mt-4 text-xs uppercase tracking-wider text-gray-500">Setup</div>
        <div className="flex flex-wrap gap-2 mt-1">
          {(bay ? BAY_SETUPS : secs[0]?.floor_space ? [...PALLET_SETUPS, 'empty'] : PALLET_SETUPS).map(s => <button key={s} onClick={() => setSetup(s)} className={btn(u.setup === s)}>{LABEL[s]}</button>)}
        </div>

        {bay && <>
          <div className="mt-4 text-xs uppercase tracking-wider text-gray-500">Shelf system</div>
          <div className="flex gap-2 mt-1">
            {['HI280', 'R3000'].map(s => <button key={s} onClick={() => setU({ system: s })} className={btn(u.system === s)}>{s}</button>)}
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-300">
            <span className="text-xs uppercase tracking-wider text-gray-500 w-24">Shelves</span>
            <button onClick={() => setShelves((u.shelves ?? 1) - 1)} className="px-3 py-2 rounded bg-gray-700">−</button>
            <b className="text-white text-lg w-8 text-center">{u.shelves ?? '?'}</b>
            <button onClick={() => setShelves((u.shelves ?? 0) + 1)} className="px-3 py-2 rounded bg-gray-700">+</button>
            <span className="text-xs text-gray-500">incl. the top (restock) shelf</span>
          </div>
          {u.levels_cm.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-gray-500 mb-1">Level heights, cm — top of shelf to top of the next (the first from the floor)</div>
              <div className="flex flex-wrap gap-2">
                {u.levels_cm.map((x, i) => (
                  <label key={i} className="text-xs text-gray-400 flex flex-col">
                    {i === 0 ? 'Floor → 1' : `${i} → ${i + 1}`}
                    <input inputMode="decimal" value={x ?? ''} onChange={e => {
                      const t = e.target.value.replace(',', '.'); const n = t === '' ? null : parseFloat(t);
                      const lv = [...u.levels_cm]; lv[i] = n !== null && Number.isFinite(n) ? n : null; setU({ levels_cm: lv });
                    }} className="w-20 bg-gray-900 border border-gray-600 rounded px-2 py-2 text-white text-base" />
                  </label>
                ))}
              </div>
            </div>
          )}
          {u.system === 'R3000' && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-gray-500 w-24">Cross bracing</span>
              <button onClick={() => setU({ cross_bracing: true })} className={btn(u.cross_bracing === true)}>Yes</button>
              <button onClick={() => setU({ cross_bracing: false })} className={btn(u.cross_bracing === false)}>No</button>
            </div>
          )}
          {u.system === 'HI280' && (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-300">
              <span className="text-xs uppercase tracking-wider text-gray-500 w-24">Back beams</span>
              <button onClick={() => setU({ beams: Math.max(0, (u.beams ?? 1) - 1) })} className="px-3 py-2 rounded bg-gray-700">−</button>
              <b className="text-white text-lg w-8 text-center">{u.beams ?? '?'}</b>
              <button onClick={() => setU({ beams: (u.beams ?? 0) + 1 })} className="px-3 py-2 rounded bg-gray-700">+</button>
            </div>
          )}
        </>}

        <input value={u.note} onChange={e => setU({ note: e.target.value })} placeholder="Note (anything special about this unit)"
          className="mt-4 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
      </div>

      {err && <div className="text-red-400 text-sm mt-2">{err}</div>}
      {/* on a phone the actions stay under the thumb (2026-09-28) */}
      <div className="flex flex-wrap gap-2 mt-4 fixed md:static bottom-0 left-0 right-0 z-30 p-3 md:p-0 bg-[#0f1117] md:bg-transparent border-t md:border-0 border-gray-800">
        <button onClick={next} className="flex-1 min-w-[220px] py-4 rounded-xl bg-emerald-600 text-white text-lg font-semibold">
          ✓ Next {bay ? 'bay' : 'pallet'} {pos + 1 < units.length ? '(starts as a copy of this one)' : ''}
        </button>
        <button onClick={finish} className="px-5 py-3 rounded-xl bg-blue-600 text-white font-semibold">Finish…</button>
      </div>

      <details className="mt-6 rounded-xl border border-gray-700 p-3">
        <summary className="text-sm text-gray-300 cursor-pointer">Gaps on this face ({liveGaps.length}) — from the measurements</summary>
        <div className="mt-2 space-y-2">
          {liveGaps.map((g, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-gray-300 w-40">{g.after_code ? displayLocation(g.after_code) : '—'} | {g.before_code ? displayLocation(g.before_code) : 'toward the top wall'}</span>
              <input inputMode="decimal" value={g.length_cm ?? ''} onChange={e => { const n = parseFloat(e.target.value.replace(',', '.')); editGap(i, { length_cm: Number.isFinite(n) ? n : null }); }}
                className="w-20 bg-gray-900 border border-gray-600 rounded px-2 py-1 text-white" /> cm
              <select value={g.reason} onChange={e => editGap(i, { reason: e.target.value })} className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-white">
                {v.gap_reasons.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <input value={g.note ?? ''} onChange={e => editGap(i, { note: e.target.value })} placeholder="note" className="flex-1 min-w-[120px] bg-gray-900 border border-gray-600 rounded px-2 py-1 text-white" />
              <button onClick={() => setGaps(liveGaps.filter((_, k) => k !== i))} className="text-red-400 text-xs">remove</button>
            </div>
          ))}
          <button onClick={() => {
            const last = v.sections[starts[pos] + u.count - 1]; const nxt = v.sections[starts[pos] + u.count];
            setGaps([...liveGaps, { after_code: last?.code || null, before_code: nxt?.code || null, length_cm: null, reason: 'other', note: '' }]);
          }} className="px-3 py-2 rounded-lg bg-gray-800 text-gray-200 text-sm">+ Gap after this {bay ? 'bay' : 'pallet'}</button>
        </div>
      </details>
    </div>
  );
}
